import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { secondsToMinutes } from 'date-fns';

import { CloseIcon } from 'assets/images';
import FullscreenIcon from 'assets/images/svgs/Player/ico_fullscreen.svg';
import ExitFullscreenIcon from 'assets/images/svgs/Player/ico_fullscreen_reverse.svg';
import Live from 'assets/images/svgs/Player/ico_live.svg';
import MiniPlayerIcon from 'assets/images/svgs/Player/ico_mini_player.svg';
import Startover from 'assets/images/svgs/Player/ico_startover.svg';
import Subtitle from 'assets/images/svgs/Player/ico_subtitles.svg';
import VolumeOff from 'assets/images/svgs/Player/ico_volume_off.svg';
import VolumeOn from 'assets/images/svgs/Player/ico_volume_on.svg';
import StartRecord from 'assets/images/svgs/Recording/ico_start_record.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    PLAYER_AUDIO_SUBTITLES_BUTTON,
    PLAYER_CLOSE_BUTTON,
    PLAYER_EXIT_FULL_SCREEN_ICON,
    PLAYER_FULL_SCREEN_ICON,
    PLAYER_JUMP_TO_LIVE_BUTTON,
    PLAYER_RECORDING_ACTION_ID,
    PLAYER_REPLAY_BUTTON,
    PLAYER_VOLUME_OFF_ICON,
    PLAYER_VOLUME_ON_ICON,
} from 'config/testID';

import { selectLayoutConfig } from 'store/config/selectors';
import { PlayerTracks } from 'store/player/reducer.types';
import {
    selectIfCollectionSuspensionActive,
    selectIfKidsProfileActive,
} from 'store/user/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';
import { VideoItem } from 'services/data-manager/types';

import isFunction from 'utils/function';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import useProgramDateStr from 'utils/hooks/useProgramDateStr';
import { UseRecordingsHookReturnType } from 'utils/hooks/useRecordings';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';

import { isAudioAndSubtitles } from 'screens/PlayerModal/utils';

import { getParentalRatingAccessibilityText } from 'components/ContentMarkerIcons/ParentalRatingMarker/utils';
import { getRecordingStatusAccessibilityLabel } from 'components/ContentMarkerIcons/RecordingMarker/utils';
import SvgButton from 'components/CustomButtons/SvgButton';
import { getMarkerTextKey } from 'components/Marker/utils';

import TopChannelInfo from '../TopChannelInfo';
// @ts-ignore
import TopEventInfo from '../TopEventInfo';
import TopMetadataInfo from '../TopMetadataInfo';
import WebFullscreenControls from '../WebFullscreenControls';
import usePlayerProgramTitle from '../hooks/usePlayerProgramTitle';
import styles from './styles';

type Props = {
    videoItem?: VideoItem;
    duration: number | undefined;
    jumpToLiveAllowed: boolean;
    showOverlay: boolean;
    isClosing: boolean;
    showRecordingActionsButton?: boolean;
    startoverButtonVisible: boolean;
    playerTracks?: PlayerTracks;
    fullscreen: boolean;
    embedded?: boolean;
    isCatchup?: boolean;
    isVod?: boolean;
    onCloseModal: () => void;
    onOpenDetails?(): void;
    onJumpToLive?(): void;
    onStartover(): void;
    onFullscreenPress(): void;
    onMinimizePress(): void;
    onPictureInPicturePress?(): void;
    onSubtitlePress?(position: {
        x: number;
        y: number;
        width: number;
        height: number;
    }): void;
    onRecordingActionPress?(position: {
        x: number;
        y: number;
        width: number;
        height: number;
    }): void;
    recordingItem?: UseRecordingsHookReturnType['recordingItem'];
    muted: boolean;
    onToggleMute(muted?: boolean): void;
    noTitleBlock?: boolean;
};

const {
    ACCESSIBILITY: {
        PLAYER: {
            ACCESSIBILITY_PLAYER_MAXIMIZE,
            ACCESSIBILITY_PLAYER_MINIMIZE,
            ACCESSIBILITY_PLAYER_AUDIO_AND_SUBTITLE,
            ACCESSIBILITY_PLAYER_RECORD,
            ACCESSIBILITY_PLAYER_STARTOVER,
            ACCESSIBILITY_PLAYER_JUMP_TO_LIVE,
            ACCESSIBILITY_PLAYER_CLOSE,
            ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE_WEB,
        },
        DETAIL_PAGE: { ACCESSIBILITY_ICON_CATCHUP },
    },
    PROGRAM_DETAILS: { SCREEN_DETAIL_DURATION_MIN },
} = LANGUAGE_KEYS;

function TopOverlaySection({
    videoItem,
    duration,
    isCatchup,
    isVod,
    jumpToLiveAllowed,
    startoverButtonVisible,
    playerTracks,
    showOverlay,
    isClosing = false,
    fullscreen = true,
    embedded = false,
    showRecordingActionsButton = false,
    onCloseModal,
    onOpenDetails,
    onJumpToLive,
    onStartover,
    onFullscreenPress,
    onMinimizePress,
    onPictureInPicturePress,
    onSubtitlePress,
    onRecordingActionPress,
    recordingItem,
    muted,
    onToggleMute,
    noTitleBlock,
}: Props) {
    const { t } = useTranslation();

    const subtitleButtonRef = useRef<View>(null);
    const recordingsActionButtonRef = useRef<View>(null);
    const accessibleViewRef = useRef(null);

    const { primary, secondary } = useSelector(selectLayoutConfig);

    const profileParentalControlRating = useProfileParentalRating();
    const collectionSuspensionActive = useSelector(
        selectIfCollectionSuspensionActive,
    );
    const kidsProfileActive = useSelector(selectIfKidsProfileActive);

    const isContentLocked = useMemo(
        () =>
            needsToLockContent(
                profileParentalControlRating,
                videoItem?.parentalRating,
            ),
        [profileParentalControlRating, videoItem],
    );
    const programScheduleTimeText = useProgramDateStr(
        videoItem?.originalProgram,
    );
    const assetTitle = usePlayerProgramTitle(videoItem);

    const isEmbeddedPlayer = embedded && !fullscreen;

    const handleSubtitlePress = useCallback(() => {
        if (subtitleButtonRef?.current && isFunction(onSubtitlePress)) {
            subtitleButtonRef.current.measureInWindow(
                (x: number, y: number, width: number, height: number) => {
                    onSubtitlePress({ x, y, width, height });
                },
            );
        }
    }, [onSubtitlePress]);

    const handleRecordingPress = useCallback(() => {
        if (
            recordingsActionButtonRef?.current &&
            isFunction(onRecordingActionPress)
        ) {
            recordingsActionButtonRef.current.measureInWindow(
                (x: number, y: number, width: number, height: number) => {
                    onRecordingActionPress({ x, y, width, height });
                },
            );
        }
    }, [onRecordingActionPress]);

    const handleToggleMute = useCallback(() => {
        onToggleMute(!muted);
    }, [muted, onToggleMute]);

    useEffect(() => {
        if (showOverlay) {
            setAccessibilityFocusTo(accessibleViewRef);
        }
    }, [showOverlay, fullscreen]);

    const accessibilityLabel = useMemo(() => {
        const labels: Array<string> = [];

        if (videoItem?.channelTitle) {
            labels.push(videoItem?.channelTitle);
        }

        if (assetTitle) {
            labels.push(assetTitle);
        }

        if (videoItem?.startTime && videoItem?.endTime) {
            labels.push(
                t(getMarkerTextKey(videoItem.startTime, videoItem.endTime)),
            );
        }

        labels.push(
            t(getParentalRatingAccessibilityText(videoItem?.parentalRating)),
        );

        if (recordingItem) {
            labels.push(
                t(
                    getRecordingStatusAccessibilityLabel({
                        recordingItem,
                        collectionSuspensionActive,
                    }),
                ),
            );
        } else if ((startoverButtonVisible && !isCatchup) || isCatchup) {
            labels.push(t(ACCESSIBILITY_ICON_CATCHUP));
        }

        if (isVod && duration != null && duration > 0) {
            labels.push(
                `${secondsToMinutes(duration)} ${t(
                    SCREEN_DETAIL_DURATION_MIN,
                )}`,
            );
        } else {
            labels.push(programScheduleTimeText);
        }

        labels.push(t(ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE_WEB));

        return labels.join(', ');
    }, [
        videoItem?.channelTitle,
        videoItem?.startTime,
        videoItem?.endTime,
        videoItem?.parentalRating,
        assetTitle,
        t,
        recordingItem,
        startoverButtonVisible,
        isCatchup,
        isVod,
        duration,
        collectionSuspensionActive,
        programScheduleTimeText,
    ]);

    useEffect(() => {
        if (showOverlay) {
            const elements = document.querySelectorAll(
                `div[data-class="metadata-info"]`,
            );

            elements.forEach((element) => {
                element.setAttribute('inert', 'true');
                element.setAttribute('aria-hidden', 'true');
            });
        }
    }, [showOverlay]);

    if (!showOverlay) {
        return null;
    }

    return (
        <View style={styles.root}>
            {noTitleBlock ? (
                <View style={gStyles.flex} />
            ) : (
                <Pressable
                    style={styles.info}
                    ref={accessibleViewRef}
                    role="group"
                    accessibilityLabel={accessibilityLabel}
                    onPress={onOpenDetails}
                >
                    <TopChannelInfo
                        videoItem={videoItem}
                        isEmbeddedTabletPlayer={false}
                        isEmbeddedWebPlayer={isEmbeddedPlayer}
                    />
                    <TopEventInfo
                        videoItem={videoItem}
                        isEmbeddedTabletPlayer={false}
                        isEmbeddedWebPlayer={isEmbeddedPlayer}
                        onOpenDetails={onOpenDetails}
                        isContentLocked={isContentLocked && embedded}
                    />
                    <TopMetadataInfo
                        embedded={embedded}
                        isCatchup={isCatchup}
                        isVod={isVod}
                        duration={duration}
                        isEmbeddedTabletPlayer={false}
                        videoItem={videoItem}
                        startoverButtonVisible={startoverButtonVisible}
                        primaryColor={primary}
                        secondaryColor={secondary}
                        isContentLocked={isContentLocked && embedded}
                        collectionSuspensionActive={collectionSuspensionActive}
                        recordingItem={recordingItem}
                    />
                </Pressable>
            )}
            <View style={styles.row}>
                {isEmbeddedPlayer ? (
                    <View
                        style={[
                            styles.topTabletEmbeddedButtonsRow,
                            styles.topButtonsRowEmbeddedWeb,
                        ]}
                    >
                        {onPictureInPicturePress ? (
                            <SvgButton
                                accessibilityRole="button"
                                accessibilityLabel={t(
                                    ACCESSIBILITY_PLAYER_MINIMIZE,
                                )}
                                style={styles.topTabletEmbeddedButton}
                                svgStyle={styles.topTabletEmbeddedButtonSvg}
                                Svg={MiniPlayerIcon}
                                onPress={onPictureInPicturePress}
                            />
                        ) : (
                            <View />
                        )}
                        <SvgButton
                            accessibilityRole="button"
                            accessibilityLabel={t(
                                `ACCESSIBILITY_PLAYER_${
                                    muted ? 'UN' : ''
                                }MUTE_BUTTON`,
                            )}
                            style={styles.topTabletEmbeddedButton}
                            svgStyle={styles.topTabletEmbeddedButtonSvg}
                            Svg={muted ? VolumeOff : VolumeOn}
                            onPress={handleToggleMute}
                            testID={
                                muted
                                    ? PLAYER_VOLUME_OFF_ICON
                                    : PLAYER_VOLUME_ON_ICON
                            }
                        />
                        <SvgButton
                            accessibilityRole="button"
                            accessibilityLabel={t(
                                ACCESSIBILITY_PLAYER_MAXIMIZE,
                            )}
                            style={styles.topTabletEmbeddedButton}
                            svgStyle={styles.topTabletEmbeddedButtonSvg}
                            Svg={FullscreenIcon}
                            onPress={onFullscreenPress}
                            testID={PLAYER_FULL_SCREEN_ICON}
                        />
                    </View>
                ) : (
                    <View style={styles.topButtonsRow}>
                        {showRecordingActionsButton && !kidsProfileActive && (
                            <SvgButton
                                accessibilityRole="button"
                                accessibilityLabel={t(
                                    ACCESSIBILITY_PLAYER_RECORD,
                                )}
                                ref={recordingsActionButtonRef}
                                style={styles.topButton}
                                svgStyle={styles.topButtonSvg}
                                Svg={StartRecord}
                                onPress={handleRecordingPress}
                                testID={PLAYER_RECORDING_ACTION_ID}
                            />
                        )}
                        {startoverButtonVisible && (
                            <SvgButton
                                accessibilityRole="button"
                                accessibilityLabel={t(
                                    ACCESSIBILITY_PLAYER_STARTOVER,
                                )}
                                style={styles.topButton}
                                svgStyle={styles.topButtonSvg}
                                Svg={Startover}
                                onPress={onStartover}
                                testID={PLAYER_REPLAY_BUTTON}
                            />
                        )}
                        {jumpToLiveAllowed && (
                            <SvgButton
                                accessibilityRole="button"
                                accessibilityLabel={t(
                                    ACCESSIBILITY_PLAYER_JUMP_TO_LIVE,
                                )}
                                style={styles.topButton}
                                svgStyle={styles.topButtonSvg}
                                Svg={Live}
                                onPress={onJumpToLive}
                                testID={PLAYER_JUMP_TO_LIVE_BUTTON}
                            />
                        )}
                        {isAudioAndSubtitles(playerTracks) && (
                            <SvgButton
                                accessibilityRole="button"
                                accessibilityLabel={t(
                                    ACCESSIBILITY_PLAYER_AUDIO_AND_SUBTITLE,
                                )}
                                style={styles.topButtonAudioSubtitles}
                                svgStyle={styles.topButtonAudioSubtitlesSvg}
                                ref={subtitleButtonRef}
                                Svg={Subtitle}
                                onPress={handleSubtitlePress}
                                testID={PLAYER_AUDIO_SUBTITLES_BUTTON}
                            />
                        )}
                        <SvgButton
                            accessibilityRole="button"
                            accessibilityLabel={t(
                                `ACCESSIBILITY_PLAYER_${
                                    muted ? 'UN' : ''
                                }MUTE_BUTTON`,
                            )}
                            style={styles.topButton}
                            svgStyle={styles.topButtonSvg}
                            Svg={muted ? VolumeOff : VolumeOn}
                            onPress={handleToggleMute}
                            testID={
                                muted
                                    ? PLAYER_VOLUME_OFF_ICON
                                    : PLAYER_VOLUME_ON_ICON
                            }
                        />
                        {(!embedded || !fullscreen) && (
                            <WebFullscreenControls
                                fullscreen={fullscreen}
                                onFullscreenPress={onFullscreenPress}
                                onMinimizePress={onMinimizePress}
                            />
                        )}
                    </View>
                )}
                {!isClosing && !embedded && (
                    <SvgButton
                        accessibilityRole="button"
                        accessibilityLabel={t(ACCESSIBILITY_PLAYER_CLOSE)}
                        Svg={CloseIcon}
                        style={styles.closeButton}
                        svgStyle={styles.closeSvg}
                        onPress={onCloseModal}
                        testID={PLAYER_CLOSE_BUTTON}
                    />
                )}
                {fullscreen && embedded && (
                    <SvgButton
                        accessibilityRole="button"
                        accessibilityLabel={t(ACCESSIBILITY_PLAYER_MINIMIZE)}
                        Svg={ExitFullscreenIcon}
                        style={styles.exitFullscreenButton}
                        svgStyle={styles.exitFullscreenSvg}
                        onPress={onCloseModal}
                        testID={PLAYER_EXIT_FULL_SCREEN_ICON}
                    />
                )}
            </View>
        </View>
    );
}

export default TopOverlaySection;
